import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCard, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  disconnectLink,
  getPaymentsSettings,
  pollLinkConnect,
  startLinkConnect,
  type LinkConnectStart,
  type PaymentRecord,
} from "../../../payments/api";

export const Route = createFileRoute("/_workspace/settings/payments")({
  component: PaymentsPane,
});

const PAYMENTS_KEY = ["payments-settings"] as const;

function PaymentsPane() {
  const settings = useQuery({
    queryKey: PAYMENTS_KEY,
    queryFn: getPaymentsSettings,
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Payments</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Connect your{" "}
        <a
          href="https://app.link.com"
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          Stripe Link
        </a>{" "}
        wallet so Heath can request one-time payment cards. You approve every
        payment from the{" "}
        <a
          href="https://link.com/agents"
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          Link app
        </a>
        ; your saved cards never leave Link.
      </p>

      <section className="mt-6">
        {settings.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : settings.error ? (
          <p className="text-sm text-destructive">
            {(settings.error as Error).message}
          </p>
        ) : settings.data?.connection ? (
          <ConnectedCard
            label={settings.data.connection.label}
            createdAt={settings.data.connection.createdAt}
          />
        ) : (
          <ConnectFlow />
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-semibold">Payment history</h2>
        <div className="mt-3">
          {settings.data && settings.data.payments.length > 0 ? (
            <PaymentsTable payments={settings.data.payments} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No payments yet. When Heath requests a payment card in chat, it
              shows up here.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function ConnectedCard({
  label,
  createdAt,
}: {
  label: string | null;
  createdAt: number;
}) {
  const qc = useQueryClient();
  const disconnect = useMutation({
    mutationFn: disconnectLink,
    onSuccess: () => qc.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
  return (
    <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
      <div className="flex items-center gap-3">
        <CreditCard className="h-5 w-5 text-muted-foreground" />
        <div>
          <div className="text-sm font-medium">{label ?? "Link wallet"}</div>
          <div className="text-xs text-muted-foreground">
            Connected {new Date(createdAt * 1000).toLocaleDateString()}
          </div>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={disconnect.isPending}
        onClick={() => disconnect.mutate()}
      >
        {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
      </Button>
    </div>
  );
}

/**
 * The device-flow connect: start yields a verification URL + short code the
 * member confirms in Link (usually on their phone); we poll until the server
 * reports the tokens are sealed. The poll cadence honors the interval the
 * endpoint asked for.
 */
function ConnectFlow() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<LinkConnectStart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: startLinkConnect,
    onSuccess: (s) => {
      setError(null);
      setPending(s);
    },
    onError: (err) => setError((err as Error).message),
  });

  useEffect(() => {
    if (!pending) return;
    let stopped = false;
    // The window expiry bounds the wait on EVERY path — including a poll
    // route that keeps throwing (expired session, server down), which would
    // otherwise leave "Waiting for confirmation…" spinning forever.
    const expire = () => {
      setPending(null);
      setError("The connection window expired. Try again.");
    };
    const timer = setInterval(async () => {
      try {
        const result = await pollLinkConnect(pending.pendingId);
        if (stopped) return;
        if (result.status === "pending") {
          if (Date.now() > pending.expiresAt) expire();
          else setError(null);
          return;
        }
        setPending(null);
        if (result.status === "connected") {
          setError(null);
          void qc.invalidateQueries({ queryKey: PAYMENTS_KEY });
        } else if (result.status === "denied") {
          setError("The connection was declined in Link.");
        } else if (result.status === "error") {
          setError(result.error);
        } else {
          setError("The connection window expired. Try again.");
        }
      } catch (err) {
        if (stopped) return;
        // A transient poll failure keeps waiting (shown inline in the
        // pending card); the device code is still valid server-side.
        if (Date.now() > pending.expiresAt) expire();
        else setError((err as Error).message);
      }
    }, pending.intervalMs);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [pending, qc]);

  if (pending) {
    return (
      <div className="rounded-md border border-border px-4 py-4">
        <div className="text-sm font-medium">Confirm in Link</div>
        <p className="mt-1 text-sm text-muted-foreground">
          Open the link below and confirm this code to connect your wallet:
        </p>
        <div className="mt-3 flex items-center gap-4">
          <code className="rounded bg-muted px-3 py-1.5 text-base font-semibold tracking-wider">
            {pending.userCode}
          </code>
          <a
            href={pending.verificationUriComplete ?? pending.verificationUri}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary underline"
          >
            {pending.verificationUri.replace(/^https:\/\//, "")}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Waiting for confirmation… this page updates automatically.
        </p>
        {error ? (
          <p className="mt-1 text-xs text-destructive">
            {error} — still trying.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border px-4 py-4">
      <div className="text-sm font-medium">No Link wallet connected</div>
      <p className="mt-1 text-sm text-muted-foreground">
        Payments need a{" "}
        <a
          href="https://app.link.com"
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          Stripe Link account
        </a>{" "}
        (US only for now) with a saved payment method. Connecting lets Heath
        request one-time cards that you approve from the Link app — Heath never
        sees your real card.
      </p>
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
      <Button
        className="mt-3"
        size="sm"
        disabled={start.isPending}
        onClick={() => start.mutate()}
      >
        {start.isPending ? "Starting…" : "Connect Link wallet"}
      </Button>
    </div>
  );
}

function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

const STATUS_LABELS: Record<string, string> = {
  created: "Requested",
  pending_approval: "Awaiting approval",
  approved: "Approved",
  card_issued: "Card issued",
  denied: "Declined",
  expired: "Expired",
  canceled: "Canceled",
  succeeded: "Completed",
  failed: "Failed",
};

function PaymentsTable({ payments }: { payments: PaymentRecord[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Merchant</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Paid by</TableHead>
          <TableHead>Card</TableHead>
          <TableHead>When</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {payments.map((p) => (
          <TableRow key={p.id}>
            <TableCell>
              <span className="font-medium">{p.merchant}</span>
              {p.testMode ? (
                <Badge variant="outline" className="ml-2">
                  test
                </Badge>
              ) : null}
            </TableCell>
            <TableCell>{formatAmount(p.amount, p.currency)}</TableCell>
            <TableCell>
              <Badge variant="secondary">
                {STATUS_LABELS[p.status] ?? p.status}
              </Badge>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {p.paidBy ?? "—"}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {p.cardLast4 ? `•••• ${p.cardLast4}` : "—"}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {new Date(p.createdAt * 1000).toLocaleString()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
